import { addDoc, collection, deleteDoc, doc, getDocs, query, updateDoc, where, writeBatch } from 'firebase/firestore';
import { db } from '../../firebase';
import { parseContents } from '../utils';
import { ProjectErrorType } from '../../utils/errors/types';
import { getProjectErrorFromRawError } from '../../utils/errors';
import { MentorShort } from '../types/mentorShort';
import { CreateMentorShortDto, UpdateMentorShortDto } from '../typeDtos/mentorShort';
import { parseMentorShorts } from './parser';

// Shorts stay in the original collection, which mentors' shorts were already uploaded to.
// It may still hold legacy Q&A clips (type 'qa'); those are skipped here.
const COLLECTION = 'mentorBonusClips';
const SHORT_TYPE = 'short';
const LEGACY_QA_TYPE = 'qa';

export const getAllMentorShorts = async (): Promise<MentorShort[]> => {
  try {
    const rawResult = await getDocs(query(collection(db, COLLECTION)));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const items = (parseContents(rawResult) as any[]).filter((item) => item?.type !== LEGACY_QA_TYPE);
    return await parseMentorShorts(items);
  } catch (error) {
    console.log('[MENTOR SHORTS] Error at getAllMentorShorts:', error);
    return [];
  }
};

// Order is 0-based per mentor (like mentor boards). Returns the next free slot
// after the mentor's current last short, read fresh from Firestore.
export const getNextMentorShortOrder = async (authorId: string): Promise<number> => {
  const rawResult = await getDocs(query(collection(db, COLLECTION), where('authorId', '==', authorId)));
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const items = parseContents(rawResult) as any[];
  return items.reduce((max, item) => (typeof item?.order === 'number' ? Math.max(max, item.order) : max), -1) + 1;
};

// Persists the given id sequence (one mentor's shorts) as the display order (0, 1, 2, ...).
export const reorderMentorShorts = async (ids: string[]): Promise<ProjectErrorType | { success: boolean }> => {
  try {
    const batch = writeBatch(db);
    const updatedAt = Date.now();
    ids.forEach((id, index) => batch.update(doc(db, COLLECTION, id), { order: index, updatedAt }));
    await batch.commit();
    return { success: true };
  } catch (e) {
    console.log('[MENTOR SHORTS] Error at reorderMentorShorts:', e);
    return getProjectErrorFromRawError(e as Error);
  }
};

export const createMentorShort = async (
  data: CreateMentorShortDto,
): Promise<ProjectErrorType | { success: boolean }> => {
  try {
    const now = Date.now();
    await addDoc(collection(db, COLLECTION), { ...data, type: SHORT_TYPE, createdAt: now, updatedAt: now });
    return { success: true };
  } catch (e) {
    console.log('[MENTOR SHORTS] Error at createMentorShort:', e);
    return getProjectErrorFromRawError(e as Error);
  }
};

export const updateMentorShort = async (
  data: UpdateMentorShortDto,
): Promise<ProjectErrorType | { success: boolean }> => {
  try {
    const { id, ...rest } = data;
    await updateDoc(doc(db, COLLECTION, id), { ...rest, updatedAt: Date.now() });
    return { success: true };
  } catch (e) {
    console.log('[MENTOR SHORTS] Error at updateMentorShort:', e);
    return getProjectErrorFromRawError(e as Error);
  }
};

export const deleteMentorShort = async (id: string): Promise<ProjectErrorType | { success: boolean }> => {
  try {
    await deleteDoc(doc(db, COLLECTION, id));
    return { success: true };
  } catch (e) {
    console.log('[MENTOR SHORTS] Error at deleteMentorShort:', e);
    return getProjectErrorFromRawError(e as Error);
  }
};
